<script lang="ts" setup>
import { ref, computed, watchEffect, type Ref } from 'vue'
import { useAnimatedNumber } from '../composables/useAnimatedNumber'
import { createCurrencyFormatter } from '../utils/currency'

export type SeriesItem = {
  name: string
  value: number
  color: string
  textColor: string
}

interface Props {
  currency?: string
  locale?: string
  series: SeriesItem[]
  showPercent?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'USD',
  locale: 'en-US',
  showPercent: true,
  loading: false,
})

// -----------------------------------------------------------------------------
// Synthetic loading data
// -----------------------------------------------------------------------------

const generateRandomPercents = (count: number): number[] => {
  if (count <= 0) return []
  const raw = Array.from({ length: count }, () => Math.random())
  const sum = raw.reduce((a, b) => a + b, 0)
  return raw.map((x) => (x / sum) * 100)
}

const loadingSeries = ref<SeriesItem[]>([])

const loadingGrays = ['#6b728090', '#6b728080', '#6b728070', '#6b728060', '#6b728050']

watchEffect(() => {
  if (!props.loading) return
  const percents = generateRandomPercents(props.series.length)
  loadingSeries.value = props.series.map((s, i) => ({
    ...s,
    value: percents[i],
    color: loadingGrays[i % loadingGrays.length],
    textColor: '#9ca3af',
  }))
})

const effectiveSeries = computed(() => (props.loading ? loadingSeries.value : props.series))

// -----------------------------------------------------------------------------
// Series normalization
// -----------------------------------------------------------------------------

const total = computed(() => effectiveSeries.value.reduce((sum, s) => sum + s.value, 0))

const normalized = computed(() =>
  effectiveSeries.value.map((s) => ({
    ...s,
    percent: total.value > 0 ? (s.value / total.value) * 100 : 0,
  })),
)

const chartSeries = computed(() => normalized.value.filter((s) => s.value > 0))

// -----------------------------------------------------------------------------
// Animations
// -----------------------------------------------------------------------------

const animatedPercents = computed(() => {
  if (props.loading) return {}

  return normalized.value.reduce<Record<string, Ref<number>>>((acc, s) => {
    acc[s.name] = useAnimatedNumber(() => s.percent)
    return acc
  }, {})
})

const animatedValues = computed(() => {
  if (props.loading) return []

  return effectiveSeries.value.map((_s, i) =>
    useAnimatedNumber(() =>
      typeof effectiveSeries.value[i]?.value === 'number' ? effectiveSeries.value[i]?.value : 0,
    ),
  )
})

// -----------------------------------------------------------------------------
// Focus
// -----------------------------------------------------------------------------

const focused = ref<string | null>(null)
const locked = ref<string | null>(null)

const focus = (name: string) => {
  if (!locked.value && !props.loading) focused.value = name
}

const clearFocus = () => {
  if (!locked.value) focused.value = null
}

const toggleLock = (name: string) => {
  if (props.loading) return
  locked.value = locked.value === name ? null : name
  focused.value = locked.value
}

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const formatCurrency = createCurrencyFormatter({
  currency: props.currency,
  locale: props.locale,
  decimals: 0,
})
</script>

<template>
  <div class="w-full cursor-default">
    <!-- Bar segments -->
    <div class="flex h-7 gap-px">
      <div
        v-for="(item, i) in chartSeries"
        :key="'bar-' + i"
        class="relative flex cursor-pointer items-center justify-center rounded-sm transition-opacity duration-150"
        :class="{ 'animate-pulse': props.loading }"
        :style="{
          width: props.loading ? `max(${item.percent}%, 2px)` : `max(${animatedPercents[item.name]?.value ?? 0}%, 2px)`,
          opacity: !focused || focused === item.name || props.loading ? 1 : 0.3,
          animationDelay: props.loading ? i * 100 + 'ms' : '0ms',
        }"
        @mouseenter="focus(item.name)"
        @mouseleave="clearFocus"
        @click="toggleLock(item.name)"
      >
        <div
          class="absolute inset-0 rounded-sm transition-transform duration-150 ease-out"
          :style="{
            backgroundColor: item.color,
            transform: !props.loading && focused === item.name ? 'scaleY(1.2)' : 'scaleY(1)',
          }"
        ></div>

        <div
          v-if="!props.loading && item.percent > 7 && props.showPercent"
          class="relative text-xs"
          :style="{ color: item.textColor }"
        >
          {{ animatedPercents[item.name].value.toFixed(1) }}<span class="opacity-50">%</span>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="mb-2 mt-6 flex flex-wrap justify-center gap-6">
      <div
        v-for="(item, i) in effectiveSeries"
        :key="'legend-' + i"
        class="flex cursor-pointer flex-col gap-2 transition-opacity duration-150"
        :style="{
          opacity: !focused || focused === item.name || loading ? 1 : 0.3,
          animationDelay: loading ? i * 100 + 'ms' : '0ms',
        }"
        @mouseenter="focus(item.name)"
        @mouseleave="clearFocus"
        @click="toggleLock(item.name)"
      >
        <div
          class="h-[2px] rounded-full"
          :class="{ 'animate-pulse': loading }"
          :style="{
            animationDelay: loading ? i * 100 + 'ms' : '0ms',
            backgroundColor: item.color,
          }"
        ></div>
        <div class="text-center" :class="{ 'opacity-25': loading }">
          <div class="text-sm font-medium text-gray-900">
            <template v-if="loading">--</template>
            <template v-else>
              {{ formatCurrency(animatedValues[i].value) }}
            </template>
          </div>
          <div class="text-xs font-medium text-gray-700">
            {{ item.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
